<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  lifecycleLabel: {
    type: String,
    required: true,
  },
  lifecycleTone: {
    type: String,
    default: 'slate',
  },
  healthLabel: {
    type: String,
    required: true,
  },
  healthTone: {
    type: String,
    default: 'slate',
  },
  readinessLabel: {
    type: String,
    required: true,
  },
  readinessPercent: {
    type: Number,
    default: 0,
  },
  readinessSummary: {
    type: String,
    default: '',
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  canTest: {
    type: Boolean,
    default: false,
  },
  isDuplicating: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['test', 'duplicate', 'delete']);

const { t } = useI18n();

const normalizedReadiness = computed(() =>
  Math.min(100, Math.max(0, Number(props.readinessPercent) || 0))
);

const toneClasses = tone => {
  const classes = {
    teal: 'bg-n-teal-9/10 text-n-teal-11',
    amber: 'bg-n-amber-9/10 text-n-amber-11',
    ruby: 'bg-n-ruby-9/10 text-n-ruby-11',
    blue: 'bg-n-brand/10 text-n-blue-11',
    slate: 'bg-n-alpha-2 text-n-slate-11',
  };
  return classes[tone] || classes.slate;
};
</script>

<template>
  <section
    class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
  >
    <div
      class="flex flex-col gap-5 border-b border-n-weak bg-gradient-to-br from-n-brand/10 via-n-solid-2 to-n-solid-2 p-5 sm:p-6 xl:flex-row xl:items-start xl:justify-between"
    >
      <div class="min-w-0">
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <span
            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="toneClasses(lifecycleTone)"
          >
            {{ lifecycleLabel }}
          </span>
          <span
            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="toneClasses(healthTone)"
          >
            <span class="size-1.5 rounded-full bg-current" aria-hidden="true" />
            {{ healthLabel }}
          </span>
        </div>
        <h1 class="truncate text-2xl font-semibold text-n-slate-12">
          {{ name }}
        </h1>
        <p
          v-if="description"
          class="mt-2 max-w-2xl text-sm leading-relaxed text-n-slate-11"
        >
          {{ description }}
        </p>
      </div>

      <div class="flex w-full flex-wrap gap-2 xl:w-auto xl:justify-end">
        <Button
          v-if="canTest"
          icon="i-lucide-play"
          :label="t('CAPTAIN_EXPERIENCE.AUTO.TESTAR')"
          class="max-sm:flex-1"
          @click="$emit('test')"
        />
        <Button
          v-if="canManage"
          color="slate"
          variant="faded"
          icon="i-lucide-copy"
          :label="t('CAPTAIN_EXPERIENCE.SCRIPT.DUPLICATE_SETTINGS')"
          :title="t('CAPTAIN_EXPERIENCE.SCRIPT.DUPLICATE_SETTINGS_HELPER')"
          :is-loading="isDuplicating"
          class="max-sm:flex-1"
          @click="$emit('duplicate')"
        />
        <Button
          v-if="canManage"
          color="ruby"
          variant="ghost"
          icon="i-lucide-trash-2"
          :aria-label="t('CAPTAIN_EXPERIENCE.AUTO.EXCLUIR')"
          :is-loading="isDeleting"
          @click="$emit('delete')"
        />
      </div>
    </div>

    <div class="grid gap-4 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div class="min-w-0">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ readinessLabel }}
            </p>
            <p v-if="readinessSummary" class="mt-1 text-xs text-n-slate-10">
              {{ readinessSummary }}
            </p>
          </div>
          <span class="text-sm font-semibold tabular-nums text-n-slate-12">
            {{
              t('CAPTAIN_EXPERIENCE.AUTO.PERCENT_VALUE', {
                value: normalizedReadiness,
              })
            }}
          </span>
        </div>
        <div
          class="mt-3 h-2 overflow-hidden rounded-full bg-n-alpha-3"
          role="progressbar"
          :aria-label="readinessLabel"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="normalizedReadiness"
        >
          <div
            class="h-full rounded-full bg-n-brand transition-[width] duration-300"
            :style="{ width: `${normalizedReadiness}%` }"
          />
        </div>
      </div>
      <div
        class="flex items-center gap-2 rounded-xl bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
      >
        <span class="i-lucide-shield-check size-4 text-n-blue-11" />
        {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.PERSISTED_STATUS') }}
      </div>
    </div>
  </section>
</template>
